import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { 
  Copy, 
  Check, 
  Users, 
  DollarSign, 
  Clock, 
  CheckCircle,
  Share2,
  TrendingUp,
  Gift,
  ExternalLink,
  Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const VIPPartnerDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/referrals/my-commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not a VIP Partner yet
        setData({ not_vip_partner: true });
      } else {
        toast.error("Error al cargar datos");
      }
    } finally {
      setLoading(false);
    }
  };

  const generateCode = async () => {
    setGenerating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/referrals/my-code`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(prev => ({ ...prev, ...response.data, not_vip_partner: false }));
      toast.success("¡Código generado exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar código");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("¡Copiado al portapapeles!");
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = () => {
    const url = `${window.location.origin}/r/${data.code}`;
    if (navigator.share) {
      navigator.share({
        title: "Divídelo - Prepara tu Visa Americana",
        text: "¡Usa mi código para preparar tu DS-160!",
        url: url
      });
    } else {
      copyToClipboard(url);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('es-MX', { 
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  // Not a VIP Partner
  if (data?.not_vip_partner) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 text-center border border-amber-200">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Programa VIP Partner
            </h2>
            <p className="text-gray-600 mb-6">
              Gana comisiones refiriendo clientes. Contacta al administrador para activar tu cuenta como VIP Partner.
            </p>
            <div className="bg-white rounded-xl p-4 text-left mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Comisiones por referido:</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-gray-600">Plan Estándar ($19.99)</span>
                  <span className="font-semibold text-emerald-600">90% → $18.00</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600">Plan Premium ($49.99)</span>
                  <span className="font-semibold text-emerald-600">50% → $25.00</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-gray-600">Plan VIP ($99.99)</span>
                  <span className="font-semibold text-emerald-600">50% → $49.99</span>
                </li>
              </ul>
            </div>
            <p className="text-sm text-gray-500">
              Escribe a <strong>info@dividelo.com</strong> para más información
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // No code yet (VIP Partner but needs to generate)
  if (!data?.code) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 text-center border border-emerald-200">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Share2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Bienvenido VIP Partner!
            </h2>
            <p className="text-gray-600 mb-6">
              Genera tu código único de referido para empezar a ganar comisiones.
            </p>
            <Button 
              onClick={generateCode}
              disabled={generating}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="lg"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Generar Mi Código
                </>
              )}
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const referralLink = `${window.location.origin}/r/${data.code}`;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mi Panel de Referidos</h1>
          <p className="text-gray-500 mt-1">Comparte tu código y gana comisiones</p>
        </div>

        {/* Referral Code Card */}
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-emerald-100 text-sm mb-1">Tu código de referido</p>
              <div className="flex items-center gap-3">
                <span className="text-3xl font-bold font-mono tracking-wider">{data.code}</span>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => copyToClipboard(data.code)}
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => copyToClipboard(referralLink)}
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copiar Link
              </Button>
              <Button 
                onClick={shareLink}
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Compartir
              </Button>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <p className="text-emerald-100 text-sm flex items-center gap-2">
              <ExternalLink className="w-4 h-4" />
              <span className="font-mono text-xs bg-white/10 px-2 py-1 rounded">{referralLink}</span>
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Referidos</p>
                <p className="text-2xl font-bold text-gray-900">{data.total_referrals || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Ganado</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(data.total_earnings)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pendiente de Pago</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(data.pending_balance)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Ya Pagado</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(data.paid_balance)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Commission Rates Info */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Tus Comisiones por Venta
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">ESTÁNDAR</p>
              <p className="text-lg font-bold text-emerald-600">$18.00</p>
              <p className="text-xs text-gray-400">90% de $19.99</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">PREMIUM</p>
              <p className="text-lg font-bold text-emerald-600">$25.00</p>
              <p className="text-xs text-gray-400">50% de $49.99</p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">VIP</p>
              <p className="text-lg font-bold text-emerald-600">$49.99</p>
              <p className="text-xs text-gray-400">50% de $99.99</p>
            </div>
          </div>
        </div>

        {/* Commissions History */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Historial de Comisiones</h2>
          </div>
          
          {!data.commissions || data.commissions.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-medium text-gray-900 mb-1">Sin referidos aún</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Comparte tu código con amigos y familiares. Cuando compren, ganarás comisión automáticamente.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comisión</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.commissions.map((commission, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{commission.referred_name}</p>
                        <p className="text-xs text-gray-500">{commission.referred_email}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{commission.product_name}</td>
                      <td className="px-4 py-3">
                        <span className="font-semibold text-emerald-600">
                          {formatCurrency(commission.commission_amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {commission.status === "pending" ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Pendiente
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Pagado
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(commission.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VIPPartnerDashboard;
